import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/KlsAdHocUpload.css';
import { getApiBase, normalizeFetchError } from '../utils/apiBase';

interface MenuSummary {
  [key: string]: number;
}

interface DriverCandidate {
  id: number;
  username: string;
  name: string;
  assigned_apartments: string[];
}

interface ApartmentCandidate {
  chart_id: number;
  apartment_name: string;
  total_count: number;
  total_house_count: number;
  menu_summary: MenuSummary;
  is_personal_delivery?: boolean;
  address?: string;
  address_source?: string;
  needs_address?: boolean;
  assigned_driver_username?: string | null;
  assigned_driver_name?: string | null;
}

interface CandidatesResponse {
  delivery_date: string;
  drivers: DriverCandidate[];
  apartments: ApartmentCandidate[];
}

interface ChartValidationSummary {
  apartment_name: string;
  chart_total_count: number;
  effective_order_count: number;
  generated_row_count: number;
  source_menu_counts: MenuSummary;
  generated_menu_counts: MenuSummary;
  menu_counts_match: boolean;
  address?: string;
  address_source?: string;
  needs_address?: boolean;
  assigned_driver_name?: string | null;
}

interface KlsSummaryResponse {
  delivery_date: string;
  apartment_names: string[];
  chart_count: number;
  row_count: number;
  source_total_count: number;
  generated_total_count: number;
  source_total_menu_counts: MenuSummary;
  generated_total_menu_counts: MenuSummary;
  total_menu_counts_match: boolean;
  phone_blank_allowed: boolean;
  missing_charts: string[];
  missing_addresses: Array<{ apartment_name: string; message: string }>;
  skipped_orders: Array<{ apartment_name: string; order_index: number; fields: string[] }>;
  missing: Array<{ row_number: number; apartment_name: string; fields: string[] }>;
  blocking_issues: string[];
  can_download: boolean;
  charts: ChartValidationSummary[];
  message: string;
}

interface RegisteredPersonalChartSummary {
  chart_id: number;
  apartment_name: string;
  chart_total_count: number;
  effective_order_count: number;
  generated_row_count: number;
}

interface RegisteredPersonalSummary {
  success: boolean;
  delivery_date: string;
  chart_count: number;
  row_count: number;
  blank_order_number: boolean;
  blank_phone: boolean;
  charts: RegisteredPersonalChartSummary[];
  missing_count: number;
  missing: Array<{ row_number: number; apartment_name?: string; fields: string[] }>;
  message: string;
}

const KLS_DELIVERY_TYPES = ['샛별배송', '하루배송', '자동'];
const KLS_DROPOFF_PLACES = ['문 앞', '택배 수령실', '공동현관(대문) 앞', '기타'];
const KLS_ENTRY_METHODS = ['공동현관 비밀번호', '경비실 호출', '자유출입', '기타'];

const getTomorrowLocal = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const sortMenus = (menuSummary: MenuSummary) => (
  Object.entries(menuSummary || {}).sort((a, b) => {
    const countDiff = Number(b[1] || 0) - Number(a[1] || 0);
    return countDiff !== 0 ? countDiff : a[0].localeCompare(b[0], 'ko');
  })
);

const buildRequestPayload = (
  deliveryDate: string,
  selectedApartments: string[],
  addressOverrides: Record<string, string>,
  deliveryType: string,
  dropoffPlace: string,
  dropoffDetail: string,
  entryMethod: string,
  entryDetail: string,
  allowBlankPhone: boolean,
) => ({
  delivery_date: deliveryDate,
  apartment_names: selectedApartments,
  address_overrides: addressOverrides,
  delivery_type: deliveryType,
  dropoff_place: dropoffPlace,
  dropoff_detail: dropoffDetail,
  entry_method: entryMethod,
  entry_detail: entryDetail,
  allow_blank_phone: allowBlankPhone,
});

const parseFilename = (header: string | null, fallback: string) => {
  if (!header) return fallback;
  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }
  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || fallback;
};

const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const statusCode = response.status;
  const bodyText = await response.text().catch(() => '');
  if (!bodyText.trim()) {
    return `${fallback} (HTTP ${statusCode})`;
  }
  try {
    const parsed = JSON.parse(bodyText);
    const detail = parsed?.detail;
    if (typeof detail === 'string') return `${detail} (HTTP ${statusCode})`;
    if (detail?.message) return `${detail.message} (HTTP ${statusCode})`;
    if (parsed?.message) return `${parsed.message} (HTTP ${statusCode})`;
  } catch {
    // plain response
  }
  return `${fallback} (HTTP ${statusCode})`;
};

const KlsAdHocUpload: React.FC = () => {
  const navigate = useNavigate();
  const apiBase = getApiBase();
  const token = localStorage.getItem('token');

  const [deliveryDate, setDeliveryDate] = useState(getTomorrowLocal());
  const [driverUsername, setDriverUsername] = useState('');
  const [candidates, setCandidates] = useState<ApartmentCandidate[]>([]);
  const [drivers, setDrivers] = useState<DriverCandidate[]>([]);
  const [selectedApartments, setSelectedApartments] = useState<string[]>([]);
  const [addressOverrides, setAddressOverrides] = useState<Record<string, string>>({});
  const [deliveryType, setDeliveryType] = useState('샛별배송');
  const [dropoffPlace, setDropoffPlace] = useState('문 앞');
  const [dropoffDetail, setDropoffDetail] = useState('');
  const [entryMethod, setEntryMethod] = useState('자유출입');
  const [entryDetail, setEntryDetail] = useState('');
  const [allowBlankPhone, setAllowBlankPhone] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [personalSummary, setPersonalSummary] = useState<RegisteredPersonalSummary | null>(null);
  const [summary, setSummary] = useState<KlsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [personalLoading, setPersonalLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [personalDownloading, setPersonalDownloading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const setStatus = (text: string, type: 'success' | 'error' = 'success') => {
    setMessage(text);
    setMessageType(type);
  };

  const fetchPersonalSummary = useCallback(async (silent = false) => {
    if (!token) {
      navigate('/login');
      return;
    }

    setPersonalLoading(true);
    try {
      const params = new URLSearchParams({ delivery_date: deliveryDate });
      const response = await fetch(`${apiBase}/api/admin/kls/manual-upload-summary?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        navigate('/login');
        return;
      }
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, '등록된 개인배송 엑셀 요약 조회 실패'));
      }
      const data: RegisteredPersonalSummary = await response.json();
      setPersonalSummary(data);
      if (!silent) {
        setStatus(data.message, data.missing_count > 0 ? 'error' : 'success');
      }
    } catch (err: any) {
      setPersonalSummary(null);
      setStatus(normalizeFetchError(err, apiBase, '등록된 개인배송 엑셀 요약을 불러오지 못했습니다.'), 'error');
    } finally {
      setPersonalLoading(false);
    }
  }, [apiBase, deliveryDate, navigate, token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchCandidates = async () => {
      setLoading(true);
      setSummary(null);
      try {
        const params = new URLSearchParams({ delivery_date: deliveryDate });
        if (driverUsername) {
          params.set('driver_username', driverUsername);
        }
        const response = await fetch(`${apiBase}/api/admin/kls/ad-hoc-candidates?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        if (!response.ok) {
          throw new Error(await readErrorMessage(response, 'KLS 후보 조회 실패'));
        }
        const data: CandidatesResponse = await response.json();
        const nextCandidates = data.apartments || [];
        setCandidates(nextCandidates);
        setDrivers(data.drivers || []);

        if (driverUsername) {
          setSelectedApartments(nextCandidates.map((item) => item.apartment_name));
        } else {
          setSelectedApartments((prev) => {
            const available = new Set(nextCandidates.map((item) => item.apartment_name));
            return prev.filter((name) => available.has(name));
          });
        }
      } catch (err: any) {
        setCandidates([]);
        setSelectedApartments([]);
        setStatus(normalizeFetchError(err, apiBase, 'KLS 후보를 불러오지 못했습니다.'), 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
    fetchPersonalSummary(true);
  }, [apiBase, deliveryDate, driverUsername, fetchPersonalSummary, navigate, token]);

  const candidateMap = useMemo(() => {
    const map = new Map<string, ApartmentCandidate>();
    candidates.forEach((candidate) => map.set(candidate.apartment_name, candidate));
    return map;
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) return candidates;
    return candidates.filter((candidate) => (
      candidate.apartment_name.toLowerCase().includes(keyword) ||
      String(candidate.assigned_driver_name || '').toLowerCase().includes(keyword) ||
      String(candidate.assigned_driver_username || '').toLowerCase().includes(keyword)
    ));
  }, [candidates, searchText]);

  const selectedCandidateList = useMemo(() => (
    selectedApartments
      .map((name) => candidateMap.get(name))
      .filter((item): item is ApartmentCandidate => Boolean(item))
  ), [candidateMap, selectedApartments]);

  const selectedTotalCount = useMemo(() => (
    selectedCandidateList.reduce((sum, candidate) => sum + Number(candidate.total_count || 0), 0)
  ), [selectedCandidateList]);

  const selectedNeedsAddress = useMemo(() => (
    selectedCandidateList.filter((candidate) => {
      const override = String(addressOverrides[candidate.apartment_name] || '').trim();
      return !override && !String(candidate.address || '').trim();
    })
  ), [addressOverrides, selectedCandidateList]);

  const toggleApartment = (apartmentName: string) => {
    setSummary(null);
    setSelectedApartments((prev) => (
      prev.includes(apartmentName)
        ? prev.filter((name) => name !== apartmentName)
        : [...prev, apartmentName]
    ));
  };

  const selectAllFiltered = () => {
    setSummary(null);
    const next = new Set(selectedApartments);
    filteredCandidates.forEach((candidate) => next.add(candidate.apartment_name));
    setSelectedApartments(Array.from(next));
  };

  const clearSelection = () => {
    setSummary(null);
    setSelectedApartments([]);
  };

  const handleAddressOverride = (apartmentName: string, value: string) => {
    setSummary(null);
    setAddressOverrides((prev) => ({
      ...prev,
      [apartmentName]: value,
    }));
  };

  const buildCleanAddressOverrides = () => {
    return Object.entries(addressOverrides).reduce<Record<string, string>>((acc, [name, value]) => {
      const trimmed = String(value || '').trim();
      if (trimmed) acc[name] = trimmed;
      return acc;
    }, {});
  };

  const validateSummary = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (selectedApartments.length === 0) {
      setStatus('아파트를 선택하세요.', 'error');
      return;
    }

    setValidating(true);
    setSummary(null);
    try {
      const response = await fetch(`${apiBase}/api/admin/kls/ad-hoc-upload-summary`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildRequestPayload(
          deliveryDate,
          selectedApartments,
          buildCleanAddressOverrides(),
          deliveryType,
          dropoffPlace,
          dropoffDetail,
          entryMethod,
          entryDetail,
          allowBlankPhone,
        )),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'KLS 엑셀 검증 실패'));
      }
      const data: KlsSummaryResponse = await response.json();
      setSummary(data);
      setStatus(data.message, data.can_download ? 'success' : 'error');
    } catch (err: any) {
      setStatus(normalizeFetchError(err, apiBase, 'KLS 엑셀 검증 중 오류가 발생했습니다.'), 'error');
    } finally {
      setValidating(false);
    }
  };

  const downloadExcel = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (selectedApartments.length === 0) {
      setStatus('아파트를 선택하세요.', 'error');
      return;
    }

    setDownloading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/kls/ad-hoc-upload-excel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildRequestPayload(
          deliveryDate,
          selectedApartments,
          buildCleanAddressOverrides(),
          deliveryType,
          dropoffPlace,
          dropoffDetail,
          entryMethod,
          entryDetail,
          allowBlankPhone,
        )),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'KLS 엑셀 다운로드 실패'));
      }
      const blob = await response.blob();
      const filename = parseFilename(
        response.headers.get('Content-Disposition'),
        `KLS_임시개인배송_${deliveryDate}.xlsx`,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setStatus('KLS 임시 개인배송 엑셀을 다운로드했습니다.');
    } catch (err: any) {
      setStatus(normalizeFetchError(err, apiBase, 'KLS 엑셀 다운로드 중 오류가 발생했습니다.'), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const downloadPersonalExcel = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    setPersonalDownloading(true);
    try {
      const params = new URLSearchParams({ delivery_date: deliveryDate });
      const response = await fetch(`${apiBase}/api/admin/kls/manual-upload-excel?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, '등록된 개인배송 엑셀 다운로드 실패'));
      }
      const blob = await response.blob();
      const filename = parseFilename(
        response.headers.get('Content-Disposition'),
        `KLS_개인배송_송장등록_${deliveryDate}.xlsx`,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setStatus('등록된 개인배송 엑셀을 다운로드했습니다. 주문번호는 공란이고 전화번호는 저장값으로 표시됩니다.');
      fetchPersonalSummary(true);
    } catch (err: any) {
      setStatus(normalizeFetchError(err, apiBase, '등록된 개인배송 엑셀 다운로드 중 오류가 발생했습니다.'), 'error');
    } finally {
      setPersonalDownloading(false);
    }
  };

  return (
    <div className="kls-page">
      <header className="kls-header">
        <button className="kls-back-btn" onClick={() => navigate('/admin')} type="button">
          대시보드
        </button>
        <div>
          <h1>KLS 개인배송 엑셀</h1>
          <p>등록된 개인배송 차트와 임시 전환 차트를 KLS 업로드 양식으로 만듭니다.</p>
        </div>
      </header>

      {message && (
        <div className={`kls-message ${messageType}`}>
          {message}
        </div>
      )}

      <section className="kls-panel">
        <div className="kls-control-grid">
          <label>
            <span>배송일</span>
            <input
              type="date"
              value={deliveryDate}
              onChange={(event) => setDeliveryDate(event.target.value)}
              className="kls-input"
            />
          </label>

          <label>
            <span>담당자</span>
            <select
              value={driverUsername}
              onChange={(event) => setDriverUsername(event.target.value)}
              className="kls-input"
            >
              <option value="">전체 차트</option>
              {drivers.map((driver) => (
                <option key={driver.id} value={driver.username}>
                  {driver.name} ({driver.username})
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>배송유형</span>
            <select value={deliveryType} onChange={(event) => setDeliveryType(event.target.value)} className="kls-input">
              {KLS_DELIVERY_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>

          <label>
            <span>수령장소</span>
            <select value={dropoffPlace} onChange={(event) => setDropoffPlace(event.target.value)} className="kls-input">
              {KLS_DROPOFF_PLACES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>

          <label>
            <span>수령장소 상세설명</span>
            <input
              value={dropoffDetail}
              onChange={(event) => setDropoffDetail(event.target.value)}
              className="kls-input"
            />
          </label>

          <label>
            <span>출입방법</span>
            <select value={entryMethod} onChange={(event) => setEntryMethod(event.target.value)} className="kls-input">
              {KLS_ENTRY_METHODS.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>

          <label>
            <span>출입방법 상세설명</span>
            <input
              value={entryDetail}
              onChange={(event) => setEntryDetail(event.target.value)}
              className="kls-input"
            />
          </label>

          <label className="kls-check-row">
            <input
              type="checkbox"
              checked={allowBlankPhone}
              onChange={(event) => setAllowBlankPhone(event.target.checked)}
            />
            <span>전화번호 공란 허용</span>
          </label>
        </div>
      </section>

      <section className="kls-panel kls-personal-panel">
        <div className="kls-section-head">
          <div>
            <h2>등록된 개인배송 엑셀</h2>
            <p>
              {deliveryDate} 개인배송 차트 {personalSummary?.chart_count ?? 0}개 · 업로드 행 {personalSummary?.row_count ?? 0}개 · 주문번호 공란 · 전화번호 표시
            </p>
          </div>
          <div className="kls-actions">
            <button type="button" className="kls-secondary-btn" onClick={() => fetchPersonalSummary(false)} disabled={personalLoading || personalDownloading}>
              {personalLoading ? '확인 중...' : '새로고침'}
            </button>
            <button
              type="button"
              className="kls-download-btn"
              onClick={downloadPersonalExcel}
              disabled={
                personalLoading ||
                personalDownloading ||
                !personalSummary ||
                personalSummary.row_count === 0 ||
                personalSummary.missing_count > 0
              }
            >
              {personalDownloading ? '다운로드 중...' : '개인배송 엑셀 다운로드'}
            </button>
          </div>
        </div>

        {personalLoading ? (
          <div className="kls-empty">등록된 개인배송 차트를 확인하는 중...</div>
        ) : personalSummary ? (
          <>
            <div className="kls-total-grid">
              <div>
                <span>개인배송 차트</span>
                <strong>{personalSummary.chart_count}</strong>
              </div>
              <div>
                <span>KLS 업로드 행</span>
                <strong>{personalSummary.row_count}</strong>
              </div>
              <div>
                <span>주문번호/전화번호</span>
                <strong>{personalSummary.blank_order_number && !personalSummary.blank_phone ? '공란/표시' : '확인'}</strong>
              </div>
              <div>
                <span>필수값 누락</span>
                <strong>{personalSummary.missing_count}</strong>
              </div>
            </div>

            {personalSummary.missing_count > 0 && (
              <div className="kls-issue-list">
                <p>KLS 필수값 누락 {personalSummary.missing_count}행이 있어 다운로드 전 보완이 필요합니다.</p>
                {personalSummary.missing.slice(0, 5).map((item) => (
                  <p key={`${item.row_number}-${item.apartment_name || ''}`}>
                    {item.apartment_name || '아파트 미확인'} {item.row_number}행: {item.fields.join(', ')}
                  </p>
                ))}
              </div>
            )}

            {personalSummary.charts.length > 0 ? (
              <div className="kls-chart-table kls-personal-chart-table">
                <div className="kls-chart-table-head">
                  <span>아파트</span>
                  <span>차트 수량</span>
                  <span>주문</span>
                  <span>행</span>
                </div>
                {personalSummary.charts.map((chart) => (
                  <div key={chart.chart_id} className="kls-chart-table-row">
                    <span>{chart.apartment_name}</span>
                    <span>{chart.chart_total_count}</span>
                    <span>{chart.effective_order_count}</span>
                    <span>{chart.generated_row_count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="kls-empty">해당 배송일에 등록된 개인배송 차트가 없습니다.</div>
            )}
          </>
        ) : (
          <div className="kls-empty">개인배송 요약을 불러오지 못했습니다.</div>
        )}
      </section>

      <section className="kls-panel kls-selection-panel">
        <div className="kls-section-head">
          <div>
            <h2>임시 개인배송 전환</h2>
            <p>{deliveryDate} 등록 차트 {candidates.length}개 · 선택 {selectedApartments.length}개 · 원본 수량 {selectedTotalCount}개</p>
          </div>
          <div className="kls-actions">
            <button type="button" className="kls-secondary-btn" onClick={selectAllFiltered} disabled={loading || filteredCandidates.length === 0}>
              보이는 항목 선택
            </button>
            <button type="button" className="kls-secondary-btn" onClick={clearSelection} disabled={selectedApartments.length === 0}>
              선택 해제
            </button>
          </div>
        </div>

        <input
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          className="kls-input kls-search"
          placeholder="아파트명 또는 담당자 검색"
        />

        {loading ? (
          <div className="kls-empty">후보를 불러오는 중...</div>
        ) : filteredCandidates.length === 0 ? (
          <div className="kls-empty">해당 조건의 차트가 없습니다.</div>
        ) : (
          <div className="kls-apartment-list">
            {filteredCandidates.map((candidate) => {
              const selected = selectedApartments.includes(candidate.apartment_name);
              const effectiveAddress = String(addressOverrides[candidate.apartment_name] || candidate.address || '').trim();
              return (
                <div key={candidate.chart_id} className={`kls-apartment-row ${selected ? 'selected' : ''} ${!effectiveAddress ? 'needs-address' : ''}`}>
                  <label className="kls-apartment-main">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleApartment(candidate.apartment_name)}
                    />
                    <span>
                      <strong>{candidate.apartment_name}</strong>
                      <small>
                        {candidate.assigned_driver_name || '미배정'} · {candidate.total_house_count || candidate.total_count}가구 · {candidate.total_count}개
                      </small>
                    </span>
                  </label>
                  <div className="kls-menu-chips">
                    {sortMenus(candidate.menu_summary).map(([menu, count]) => (
                      <span key={menu}>{menu} {count}</span>
                    ))}
                  </div>
                  {selected && (
                    <label className="kls-address-input">
                      <span>도로명 주소</span>
                      <input
                        value={effectiveAddress}
                        onChange={(event) => handleAddressOverride(candidate.apartment_name, event.target.value)}
                        className="kls-input"
                        placeholder="도로명 기본주소"
                      />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {selectedNeedsAddress.length > 0 && (
        <div className="kls-message error">
          주소 누락 {selectedNeedsAddress.length}개: {selectedNeedsAddress.map((item) => item.apartment_name).join(', ')}
        </div>
      )}

      <section className="kls-panel">
        <div className="kls-download-row">
          <button type="button" className="kls-primary-btn" onClick={validateSummary} disabled={validating || downloading || selectedApartments.length === 0}>
            {validating ? '검증 중...' : '검증'}
          </button>
          <button type="button" className="kls-download-btn" onClick={downloadExcel} disabled={downloading || validating || selectedApartments.length === 0}>
            {downloading ? '다운로드 중...' : '엑셀 다운로드'}
          </button>
        </div>
      </section>

      {summary && (
        <section className="kls-panel">
          <div className="kls-section-head">
            <div>
              <h2>검증 결과</h2>
              <p>{summary.can_download ? '다운로드 가능' : '보완 필요'} · 행 {summary.row_count}개 · 메뉴 {summary.source_total_count}개</p>
            </div>
            <span className={`kls-status-pill ${summary.can_download ? 'ok' : 'bad'}`}>
              {summary.can_download ? '정상' : '확인 필요'}
            </span>
          </div>

          <div className="kls-total-grid">
            <div>
              <span>원본 메뉴 총수</span>
              <strong>{summary.source_total_count}</strong>
            </div>
            <div>
              <span>KLS 생성 메뉴 총수</span>
              <strong>{summary.generated_total_count}</strong>
            </div>
            <div>
              <span>KLS 업로드 행</span>
              <strong>{summary.row_count}</strong>
            </div>
            <div>
              <span>메뉴 대조</span>
              <strong>{summary.total_menu_counts_match ? '일치' : '불일치'}</strong>
            </div>
          </div>

          <div className="kls-menu-compare">
            {sortMenus(summary.source_total_menu_counts).map(([menu, count]) => (
              <div key={menu} className="kls-compare-row">
                <span>{menu}</span>
                <strong>{count}</strong>
                <em>생성 {summary.generated_total_menu_counts[menu] || 0}</em>
              </div>
            ))}
          </div>

          {summary.blocking_issues.length > 0 && (
            <div className="kls-issue-list">
              {summary.missing_charts.length > 0 && <p>차트 없음: {summary.missing_charts.join(', ')}</p>}
              {summary.missing_addresses.length > 0 && <p>주소 누락: {summary.missing_addresses.map((item) => item.apartment_name).join(', ')}</p>}
              {summary.skipped_orders.length > 0 && <p>주문 필드 누락: {summary.skipped_orders.length}건</p>}
              {summary.missing.length > 0 && <p>KLS 필수값 누락: {summary.missing.length}행</p>}
              {!summary.total_menu_counts_match && <p>원본 메뉴 수량과 생성 메뉴 수량이 다릅니다.</p>}
            </div>
          )}

          <div className="kls-chart-table">
            <div className="kls-chart-table-head">
              <span>아파트</span>
              <span>원본</span>
              <span>행</span>
              <span>주소</span>
              <span>대조</span>
            </div>
            {summary.charts.map((chart) => (
              <div key={chart.apartment_name} className="kls-chart-table-row">
                <span>{chart.apartment_name}</span>
                <span>{chart.effective_order_count}</span>
                <span>{chart.generated_row_count}</span>
                <span>{chart.address_source === 'missing' ? '누락' : chart.address_source}</span>
                <span>{chart.menu_counts_match ? '일치' : '불일치'}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default KlsAdHocUpload;
